<script setup lang="ts" generic="T">
import { computed, onBeforeMount, ref } from 'vue';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/Table';

import { SingleRow, TableLoadingBlock } from '@/components/custom/CustomTable';

import { Button } from '@/components/ui/Button';

import { alphabeticalSorting, getSortingIconValue } from '@/lib/sorting';

import { cn } from '@/lib/utils';

import { Checkbox } from '@/components/custom';

import {
  type AllowedColumns,
  createTableColumns,
  DEFAULT_RENDERED_ROWS,
  SELECT_COLUMN
} from '@/constants/table';

import { SORT_ASCENDING, SORT_DESCENDING } from '@/constants/sorting';

import type { SortingOrder } from '@/types/sorting';
import {
  STATUS_DEFAULT,
  STATUS_ERROR,
  STATUS_SUCCESS
} from '@/constants/status';

import type { ValidationStatuses } from '@/types/components';

const props = defineProps<{
  columns: AllowedColumns[];
  tableData: T[];
  updatedTableData?: T[];
  defaultSorting?: AllowedColumns;
  isModal?: boolean;
  searchQuery?: string;
  isFetching: boolean;
  isSelectable?: boolean;
  selectedIds?: number[];
  rowKey?: keyof T;
  validationStatus?: ValidationStatuses;
  showErrorMessage?: boolean;
  validationMessage?: string;
}>();

onBeforeMount(() => {
  if (props.selectedIds) {
    selectedRows.value = new Set(props.selectedIds);
  }
});

const currentSortingColumn = ref<AllowedColumns | undefined>(
  props.defaultSorting
);

const currentSortingType = ref<SortingOrder>(SORT_ASCENDING);

const tableColumns = computed(() => {
  const columns = createTableColumns(props.columns);

  if (props.isSelectable) {
    return [
      {
        value: { key: SELECT_COLUMN as AllowedColumns, label: '', tooltip: '' },
        isExtended: false,
        isSortable: false
      },
      ...columns
    ];
  }

  return columns;
});

const loadingData = Array.from({
  length:
    props.isModal && !props.isSelectable
      ? props.columns.length
      : props.columns.length + 1
}).map((_, i) => i);

const sortedTableData = computed(() => {
  if (!currentSortingColumn.value) {
    return props.tableData;
  }

  return alphabeticalSorting<T>({
    array: props.tableData as T[],
    param: currentSortingColumn.value as keyof T,
    descOrder: currentSortingType.value !== SORT_ASCENDING,
    caseInsensitive: true
  });
});

const sortedUpdatedTableData = computed(() => {
  if (!currentSortingColumn.value || !props.updatedTableData) {
    return props.updatedTableData;
  }

  return alphabeticalSorting<T>({
    array: props.updatedTableData,
    param: currentSortingColumn.value as keyof T,
    descOrder: currentSortingType.value !== SORT_ASCENDING,
    caseInsensitive: true
  });
});

function onSortingChange() {
  currentSortingType.value =
    currentSortingType.value === SORT_ASCENDING
      ? SORT_DESCENDING
      : SORT_ASCENDING;
}

const emit = defineEmits<{
  (e: 'selectionChanged', selection: number[]): void;
}>();

const rowKey = props.rowKey ?? ('id' as keyof T);

const selectedRows = ref(new Set());

const allSelected = computed(() => {
  return !props.isSelectable
    ? false
    : sortedTableData.value.length > 0 &&
        sortedTableData.value.every(row => selectedRows.value.has(row[rowKey]));
});

function emitSelectionChanged() {
  emit('selectionChanged', Array.from(selectedRows.value) as number[]);
}

function toggleSelectAll(checked: boolean) {
  if (checked) {
    selectedRows.value = new Set(sortedTableData.value.map(row => row[rowKey]));
  } else {
    selectedRows.value = new Set();
  }

  emitSelectionChanged();
}

function toggleRowSelection(rowId: unknown, checked: boolean) {
  if (checked) {
    selectedRows.value.add(rowId);
  } else {
    selectedRows.value.delete(rowId);
  }

  emitSelectionChanged();
}
</script>

<template>
  <div
    :class="
      cn(
        'shadow border border-gray-200 rounded-md overflow-hidden',
        isModal &&
          'max-w-[calc(90dvw_-_3.5rem)] sm:max-w-[calc(25rem_-_3.5rem)] md:max-w-[calc(37.5rem_-_3.5rem)] xl:max-w-[calc(50rem_-_5rem)]'
      )
    "
  >
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead
            v-for="column in tableColumns"
            :key="column.value.key"
            :test-id="`custom-table-${column.value.key}-header`"
            :class="cn(column.isSortable ? 'flex items-center gap-2.5' : '')"
          >
            <template v-if="column.value.key === SELECT_COLUMN">
              <Checkbox
                test-id="custom-table-select-all-checkbox"
                :is-disabled="isFetching || !sortedTableData.length"
                :checked="allSelected"
                @change="toggleSelectAll($event.target.checked)"
              />
            </template>

            <template v-if="column.isSortable">
              <Button
                class="text-xs font-semibold text-inherit uppercase px-0 py-0 h-min"
                :is-disabled="isFetching || !sortedTableData.length"
                variant="icon"
                :test-id="`custom-table-sort-${column.value.key}-button`"
                :title="column.value.tooltip"
                @click="onSortingChange"
              >
                <span class="text-xs">{{ column.value.label }}</span>
                <component
                  :is="
                    getSortingIconValue(
                      currentSortingColumn === column.value.key
                        ? currentSortingType
                        : SORT_ASCENDING
                    )
                  "
                  :data-testid="
                    `custom-table-sort-${column.value.key}-${
                      currentSortingColumn === column.value.key
                        ? currentSortingType
                        : SORT_ASCENDING
                    }`
                  "
                  class="text-gray-800 h-4 w-4"
                />
              </Button>
            </template>

            <span v-else class="text-xs">{{ column.value.label }}</span>
          </TableHead>

          <TableHead v-if="!isModal" key="actions_head">
            <span class="text-xs">{{ $t('common.listActions') }}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody v-if="isFetching || sortedTableData.length">
        <template v-if="isFetching">
          <TableRow
            v-for="row in DEFAULT_RENDERED_ROWS"
            :key="row"
            test-id="custom-table-loading-row"
            class="hover:bg-white"
          >
            <TableCell
              v-for="cell in loadingData"
              :key="cell"
              test-id="custom-table-loading-cell"
              class="px-5 py-[11.5px]"
            >
              <TableLoadingBlock />
            </TableCell>
          </TableRow>
        </template>

        <template v-else>
          <TableRow
            v-for="(row, index) in sortedTableData"
            :key="`${row}`"
            :test-id="`custom-table-row-${index + 1}`"
          >
            <SingleRow
              v-for="column in tableColumns"
              :key="column.value.key"
              :is-modal="isModal"
              :row="row"
              :column="column"
              :updated-row="sortedUpdatedTableData?.[index]"
              :is-selectable="isSelectable"
              :is-selected="selectedRows.has(row[rowKey as keyof T])"
              @toggle-selected="
                (rowId, checked) => toggleRowSelection(rowId, checked)
              "
            />
            <TableRow v-if="!isModal" key="actions_row">
              <slot :row="row"></slot>
            </TableRow>
          </TableRow>
        </template>
      </TableBody>

      <template
        v-else-if="
          !isFetching && !sortedTableData.length && searchQuery?.length
        "
      >
        <slot name="table-no-results"></slot>
      </template>

      <template v-else>
        <slot name="table-empty"></slot>
      </template>
    </Table>
  </div>

  <p
    v-if="
      validationMessage &&
      showErrorMessage &&
      validationStatus !== STATUS_DEFAULT
    "
    data-testid="custom-table-validation-message"
    :class="
      cn(
        'text-sm mt-1',
        validationStatus === STATUS_ERROR && 'text-red-600',
        validationStatus === STATUS_SUCCESS && 'text-emerald-600'
      )
    "
  >
    {{ validationMessage }}
  </p>
</template>
